/**
 * VersionState - Detects when a new version of the app has been deployed and prompts users to refresh.
 *
 * This addresses the "Unknown widget type" error that occurs when users have a stale browser bundle
 * that doesn't recognize widget types introduced in newer deployments.
 *
 * Architecture:
 * - Build: vite.config.js generates /version.json with the current git commit hash
 * - Runtime: VersionState periodically fetches /version.json and compares with Env.APP_VERSION
 * - If different, shows a non-intrusive toast with a "Refresh" button
 *
 * Check triggers:
 * - Tab becomes visible (debounced to 30s minimum between checks)
 * - Every 5 minutes while tab is active
 */

import type { ToastState } from '@elara/client-core/states/ToastState';

import type { ToastActionElement } from '#ui/toast.js';

import * as React from 'react';
import { z } from 'zod';

import { createState } from '@elara/client-core/lib/signals-utils';

import { Env } from '#env.js';
import { ToastAction } from '#ui/toast.js';

/** Minimum time between version checks (30 seconds) */
const CHECK_DEBOUNCE_MS = 30_000;

/** Interval for periodic version checks (5 minutes) */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** Delay before showing toast after page load (5 seconds) */
const INITIAL_TOAST_DELAY_MS = 5_000;

/** Zod schema for version.json validation */
const VersionInfoSchema = z.object({ version: z.string(), buildTime: z.string() });

/**
 * Creates the version state for app update detection.
 *
 * @param toastState - The toast state for showing update notifications
 */
export function createVersionState(toastState: ToastState) {
  const state = createState({
    /** Version reported by the server's version.json */
    serverVersion: undefined as string | undefined,

    /** Whether an update has been detected */
    updateAvailable: false,

    /** Whether the toast has already been shown this session */
    toastShown: false,

    /** Timestamp of the last version check */
    lastCheckTime: 0,

    /** Reference to the periodic check interval */
    checkInterval: undefined as NodeJS.Timeout | undefined,

    /** Reference to visibility change handler for cleanup */
    visibilityHandler: undefined as (() => void) | undefined,

    /** Reference to initial check timeout for cleanup */
    initialCheckTimeout: undefined as NodeJS.Timeout | undefined,

    /** Reference to toast delay timeout for cleanup */
    toastDelayTimeout: undefined as NodeJS.Timeout | undefined,

    /** Reference to stopMonitors for cleanup */
    stopMonitors: undefined as (() => void) | undefined,

    /**
     * Checks for a new app version by fetching /version.json and comparing with current version.
     * Shows a toast notification if an update is available.
     */
    async checkForUpdate(): Promise<void> {
      const now = Date.now();

      // Debounce: skip if checked recently
      if (now - this.lastCheckTime < CHECK_DEBOUNCE_MS) {
        return;
      }

      this.lastCheckTime = now;

      try {
        // Fetch version.json with cache-busting query param
        const response = await fetch(`/version.json?t=${now}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
        });

        if (!response.ok) {
          return;
        }

        const rawData: unknown = await response.json();
        const parseResult = VersionInfoSchema.safeParse(rawData);

        if (!parseResult.success) {
          return;
        }

        const versionInfo = parseResult.data;
        this.serverVersion = versionInfo.version;

        // Compare versions
        const currentVersion = Env.APP_VERSION;
        const hasUpdate = versionInfo.version !== currentVersion && currentVersion !== '0.0.0';

        if (hasUpdate && !this.toastShown) {
          this.updateAvailable = true;
          this.showUpdateToast();
        }
      } catch {
        // Network errors are expected (offline, etc.) - silently ignore
      }
    },

    /**
     * Shows the update available toast notification.
     */
    showUpdateToast(): void {
      if (this.toastShown) return;

      this.toastShown = true;

      // Clear any existing timeout
      if (this.toastDelayTimeout) {
        clearTimeout(this.toastDelayTimeout);
      }

      // Delay showing toast slightly after page load to avoid interrupting user
      this.toastDelayTimeout = setTimeout(() => {
        // Create the refresh action button element
        // Note: The cast is needed because ToastActionElement's type is overly strict for React.createElement
        const refreshAction = React.createElement(
          ToastAction,
          {
            altText: 'Refresh to update',
            onClick: () => {
              window.location.reload();
            },
          },
          'Refresh',
        ) as unknown as ToastActionElement;

        toastState.addToast({
          title: 'Update Available',
          description: `A new version of ${Env.APP_NAME} is available. Refresh to get the latest features and make sure the app continues to work correctly.`,
          variant: 'default',
          duration: Infinity, // User decides when to refresh
          action: refreshAction,
        });
      }, INITIAL_TOAST_DELAY_MS);
    },

    /**
     * Starts monitoring for version updates.
     * Sets up visibility change listener and periodic checks.
     * Idempotent: if already running, returns existing cleanup function.
     * Disabled in development mode where version mismatches are expected.
     */
    startMonitors(): () => void {
      // Skip version monitoring in development - mismatches are expected during HMR
      if (Env.MODE === 'development') {
        return () => {};
      }

      // Idempotent: if already running, return existing cleanup
      if (this.stopMonitors) {
        return this.stopMonitors;
      }

      // Handler for visibility changes
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          void this.checkForUpdate();
        }
      };

      this.visibilityHandler = handleVisibilityChange;
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Periodic check while tab is active
      this.checkInterval = setInterval(() => {
        if (document.visibilityState === 'visible') {
          void this.checkForUpdate();
        }
      }, CHECK_INTERVAL_MS);

      // Initial check after a short delay
      this.initialCheckTimeout = setTimeout(() => {
        void this.checkForUpdate();
      }, INITIAL_TOAST_DELAY_MS);

      // Setup stopMonitors
      this.stopMonitors = () => {
        if (this.visibilityHandler) {
          document.removeEventListener('visibilitychange', this.visibilityHandler);
          this.visibilityHandler = undefined;
        }

        if (this.checkInterval) {
          clearInterval(this.checkInterval);
          this.checkInterval = undefined;
        }

        if (this.initialCheckTimeout) {
          clearTimeout(this.initialCheckTimeout);
          this.initialCheckTimeout = undefined;
        }

        if (this.toastDelayTimeout) {
          clearTimeout(this.toastDelayTimeout);
          this.toastDelayTimeout = undefined;
        }

        this.stopMonitors = undefined;
      };

      return this.stopMonitors;
    },
  });

  return state;
}

export type VersionState = ReturnType<typeof createVersionState>;
